import { TypeSections, TypeTimeline } from '@moar/types';

interface GetDataUrlsToCacheProps {
    data: TypeTimeline;
    buildId: string;
}

// The routes that have a `getStaticProps` payload, i.e. a `/_next/data/<buildId>/<route>.json`
// file. Kept in sync with the `precacheHtml/precacheJson` list in
// `libs/utils/src/lib/precache.mjs` — home (`''`) and `/offline` are intentionally
// excluded there (no props), so they're excluded here too.
const STATIC_DATA_ROUTES = ['/intro', '/about', '/glossary', '/sections', '/my-timeline', '/how-to'];

/**
 * Build the list of `/_next/data/<buildId>/*.json` URLs the app needs to work offline
 * (the static pages above + every object detail page).
 *
 * These are normally precached by serwist at service-worker install time, but that install
 * fires ~170 fetches in one burst and is all-or-nothing per entry — against a CDN/WAF a few
 * of those fetches get dropped, so those pages silently fail offline. Warming them here (from
 * the user-initiated "Load images" flow, with bounded concurrency) makes offline data robust
 * regardless of how the install burst fared. The SW caches them; `fetchCachedNextData` serves
 * them offline via a query-insensitive `caches.match`, so the trailing `?slug=`/`?tags=` on the
 * live request still matches these no-query URLs.
 */
export const getDataUrlsToCache = (props: GetDataUrlsToCacheProps): string[] => {
    const { data, buildId } = props;
    if (!buildId) return [];

    const dataUrl = (route: string) => `/_next/data/${buildId}${route}.json`;

    const getObjectSlugs = () => {
        const sections = data.data.timelineSections as TypeSections;
        const objects = sections.data
            .map((section) => section.objects.filter((obj) => obj.type !== 'timeline-section-date'))
            .flat();
        return objects.map((obj) => obj.slug).filter((slug): slug is string => typeof slug === 'string' && slug.length > 0);
    };

    const staticUrls = STATIC_DATA_ROUTES.map((route) => dataUrl(route));
    const objectUrls = getObjectSlugs().map((slug) => dataUrl(`/objects/${slug}`));

    return [...staticUrls, ...objectUrls];
};
